import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { ChartConfiguration, ChartType } from 'chart.js';
import { BaseChartDirective } from 'ng2-charts';

const DEFAULT_COLORS = [
  '#1976d2',
  '#ff6384',
  '#4caf50',
  '#ffcd56',
  '#36a2eb',
  '#9966ff',
  '#ff9f40',
  '#00acc1',
  '#8e24aa',
  '#43a047',
];

@Component({
  selector: 'app-ai-chat-chart',
  templateUrl: './ai-chat-chart.component.html',
  styleUrls: ['./ai-chat-chart.component.scss'],
})
export class AiChatChartComponent implements OnInit {
  @Input() chartData!: unknown;
  @ViewChild(BaseChartDirective) chart?: BaseChartDirective;

  chartConfig!: ChartConfiguration;
  title = '';

  ngOnInit(): void {
    this.buildChartConfig();
  }

  downloadChart(): void {
    const chart = this.chart?.chart;
    if (!chart) {
      return;
    }
    const url = chart.toBase64Image();
    const link = document.createElement('a');
    link.href = url;
    link.download = `${this.title || 'grafico'}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  private buildChartConfig(): void {
    const data = this.getChartPayload();
    const type = (data?.type || 'bar') as ChartType;
    const labels = Array.isArray(data?.labels) ? data.labels : [];
    const rawDatasets = Array.isArray(data?.datasets) ? data.datasets : [];

    this.title = data?.title || '';

    const datasets = rawDatasets.map((dataset: any, index: number) => {
      const dataPoints = Array.isArray(dataset?.data) ? dataset.data : [];
      const colorCount = dataPoints.length || labels.length || 1;
      return {
        label: dataset?.label || `Dataset ${index + 1}`,
        data: dataPoints,
        backgroundColor: dataset?.backgroundColor || this.generateColors(colorCount, index),
        borderColor: dataset?.borderColor,
        borderWidth: dataset?.borderWidth ?? 1,
        fill: dataset?.fill ?? true,
        tension: dataset?.tension ?? (type === 'line' ? 0.3 : undefined),
      };
    });

    this.chartConfig = {
      type,
      data: {
        labels,
        datasets,
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          title: { display: false },
          legend: {
            display: true,
            position: 'bottom',
            labels: {
              boxWidth: 12,
              font: { size: 12 },
            },
          },
          tooltip: {
            enabled: true,
          },
        },
        scales: this.buildScales(type),
      },
    } as ChartConfiguration;
  }

  private getChartPayload(): any {
    if (!this.chartData) {
      return null;
    }
    const data = this.chartData as any;
    return data?.chart || data;
  }

  private generateColors(count: number, datasetIndex: number): string[] {
    const offset = datasetIndex * 2;
    return Array.from(
      { length: count },
      (_, i) => DEFAULT_COLORS[(offset + i) % DEFAULT_COLORS.length]
    );
  }

  private buildScales(type: ChartType): any {
    const circularTypes: ChartType[] = ['pie', 'doughnut', 'radar', 'polarArea'];
    if (circularTypes.includes(type)) {
      return {};
    }
    return {
      x: {
        ticks: { font: { size: 11 } },
        grid: { display: false },
      },
      y: {
        beginAtZero: true,
        ticks: { font: { size: 11 } },
        grid: { color: '#f0f0f0' },
      },
    };
  }
}
